export const createSessionSlice = (set, get) => ({
  // Session and prompt state
  currentPrompt: null,
  isLoading: false,
  isTyping: false, // State to track typing activity
  isTypingPrompt: false, // State to track prompt typing specifically
  isAwaitingPrompt: false, // State to track when we're waiting for prompt to appear (during prompt delay)
  isSkippableDelay: false, // State to track if current loading is a skippable delay
  skipCurrentTyping: false, // State to track if current typing should be skipped
  inSession: false,
  currentRedirectData: null, // Store redirect command and payload information
  currentPayload: null, // Store payload/data for current command/step/redirect operations
  resetToken: 0,

  // Session state setters
  setCurrentPrompt: (newPrompt) => set({ currentPrompt: newPrompt }),
  setIsLoading: (loading) => set({ isLoading: loading }),
  setIsTyping: (typing) => set({ isTyping: typing }),
  setIsTypingPrompt: (typing) => set({ isTypingPrompt: typing }),
  setIsAwaitingPrompt: (awaiting) => set({ isAwaitingPrompt: awaiting }),
  setIsSkippableDelay: (skippable) => set({ isSkippableDelay: skippable }),
  setSkipCurrentTyping: (skip) => set({ skipCurrentTyping: skip }),
  setInSession: (session) => set({ inSession: session }),
  setCurrentRedirectData: (data) => set({ currentRedirectData: data }),
  setCurrentPayload: (payload) => set({ currentPayload: payload }),
  incrementResetToken: () =>
    set((state) => ({ resetToken: state.resetToken + 1 })),

  // Helper function to clear all command-related state
  clearCommandState: () =>
    set({
      currentRedirectData: null,
      currentPayload: null,
      inSession: false,
      currentPrompt: null,
    }),

  // Set prompt with character-by-character typing effect
  setCurrentPromptWithTypingEffect: async (newPrompt) => {
    const {
      typingSpeed,
      promptDelay,
      setSkipCurrentTyping,
      scrollToBottomByFocus,
    } = get();

    if (!newPrompt || typeof newPrompt !== "string") {
      set({ currentPrompt: newPrompt, isLoading: false });
      return Promise.resolve();
    }

    // Snapshot resetToken so we can abort cleanly if the host calls
    // incrementResetToken() (e.g. on lesson unmount or terminal reset).
    const initialResetToken = get().resetToken;
    const isAborted = () => get().resetToken !== initialResetToken;

    // Reset skip state at the beginning
    setSkipCurrentTyping(false);

    // Add delay before prompt appears (after output completion)
    if (promptDelay > 0) {
      set({ isAwaitingPrompt: true }); // Indicate we're waiting for prompt (keep isLoading as-is to prevent ">" flash)

      // Create skippable prompt delay
      const delayPromise = new Promise((resolve) =>
        setTimeout(resolve, promptDelay)
      );

      const skipPromise = new Promise((resolve) => {
        const checkSkipInterval = setInterval(() => {
          const currentState = get();
          if (currentState.skipCurrentTyping || isAborted()) {
            clearInterval(checkSkipInterval);
            if (!isAborted()) setSkipCurrentTyping(false); // Reset skip flag
            resolve(); // Skip the delay
          }
        }, 10); // Check every 10ms for skip

        // Clean up interval when delay completes normally
        delayPromise.then(() => clearInterval(checkSkipInterval));
      });

      await Promise.race([delayPromise, skipPromise]);

      if (isAborted()) return Promise.resolve();
      set({ isAwaitingPrompt: false }); // Clear waiting state
    }

    if (isAborted()) return Promise.resolve();

    // If no typing speed set, just set immediately after prompt delay
    if (typingSpeed === 0) {
      set({ currentPrompt: newPrompt, isLoading: false });
      // Auto-focus input and scroll after prompt appears
      setTimeout(() => {
        if (isAborted()) return;
        const activeInput = document.querySelector(
          'input[type="text"]:not([disabled])'
        );
        if (activeInput) {
          activeInput.focus();
        }
        // Also trigger scroll to bottom
        if (scrollToBottomByFocus) {
          scrollToBottomByFocus();
        }
      }, 50);
      return Promise.resolve();
    }

    set({ isTypingPrompt: true, isLoading: false }); // Use prompt-specific typing state and clear loading

    // Start with empty prompt and build it up
    set({ currentPrompt: "" });

    // Type out character by character
    for (let i = 1; i <= newPrompt.length; i++) {
      // Aborted by host (lesson unmount / reset) — bail without restoring text
      if (isAborted()) return Promise.resolve();

      // Check if we should skip prompt typing
      const currentState = get();
      if (currentState.skipCurrentTyping) {
        // Complete the prompt immediately
        set({ currentPrompt: newPrompt });
        // Reset skip flag
        setSkipCurrentTyping(false);
        break; // Exit character loop
      }

      await new Promise((resolve) => setTimeout(resolve, typingSpeed));
      if (isAborted()) return Promise.resolve();
      set({ currentPrompt: newPrompt.substring(0, i) });
    }

    if (isAborted()) return Promise.resolve();
    set({ isTypingPrompt: false }); // Clear prompt typing state

    // Auto-focus input after prompt typing completes
    setTimeout(() => {
      if (isAborted()) return;
      const activeInput = document.querySelector(
        'input[type="text"]:not([disabled])'
      );
      if (activeInput) {
        activeInput.focus();
      }
      // Also trigger scroll to bottom
      if (scrollToBottomByFocus) {
        scrollToBottomByFocus();
      }
    }, 50); // Small delay to ensure DOM is updated

    return Promise.resolve();
  },
});
